/**
 * 步骤 Prompt 外显管理 + 测试场
 *
 * 定位：把机翻 agent 各步骤的 prompt 从引擎硬编码中"外显"出来，让 founder/PM
 * 在前端直接查看、编辑、保存，并在测试场里用样例变量跑一次 LLM 验证效果。
 *
 * 页面结构（自上而下）：
 * 1. 外显管理：列出各步骤场景，选中后查看/编辑该步骤的 prompt 模板正文，可保存。
 * 2. 测试场：自动解析模板中的 {变量} 占位符生成输入框，填样例后调用引擎
 *    渲染 + 跑 LLM，展示渲染后的完整 prompt 与模型输出。
 * 3. 自动优化：保留原有 PromptOptimizerPanel 作为次要功能放最下方。
 *
 * 说明：未保存自定义模板的场景，前端用内置默认模板做展示兜底（与引擎默认一致），
 * 保存后以数据库模板为准；测试场未传模板正文时引擎会用自己的内置默认，保证可测。
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { promptTemplatesApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Save, Play, Loader2, FlaskConical } from 'lucide-react'
import { toast } from 'sonner'
import { PromptOptimizerPanel } from './PromptOptimizerPanel'

// agent 各步骤场景元信息（与 Workers API PROMPT_SCENES 一一对应）
const SCENES = [
  { id: 'document_analysis', name: '文档分析', description: '分析文档类型、主题、受众与语言风格，生成摘要' },
  { id: 'term_identification', name: '术语预识别', description: '识别文档中的关键术语与固定搭配' },
  { id: 'translation', name: '整篇初译', description: '执行初始翻译（含术语/上下文/参考/锚点按需注入）' },
  { id: 'reflection', name: '自我修正', description: '基于 MQM 评估对译文进行自我修正' },
  { id: 'mqm', name: 'MQM 质量评分', description: '多维度质量评估' },
  { id: 'line_alignment', name: '行数对齐重试', description: '行数不一致时的修正重试提示' },
]

// 未自定义场景的展示兜底默认模板（与引擎 DEFAULT_PROMPT_TEMPLATES 保持一致的展示副本）。
// 实际行为以引擎默认/数据库模板为准；此处仅用于"未保存时能看见当前 prompt"。
const DEFAULT_CONTENT: Record<string, string> = {
  document_analysis: `请分析以下文档并生成摘要。
当前语言对：从 {source_lang} 翻译到 {target_lang}

分析维度包括：
1. 文档类型（商务/营销/技术/字幕等）
2. 主题和目标受众
3. 语言风格（正式/非正式/专业/通俗）
4. 关键主题词汇

文档内容：
{sample_text}

输出JSON格式：
{{
  "documentType": "商务文档",
  "topic": "...",
  "targetAudience": "...",
  "style": "正式专业",
  "keyTerms": ["术语1", "术语2"]
}}`,
  term_identification: `基于以下信息，识别文档中的关键术语。
注意语言对：从 {source_lang} 翻译到 {target_lang}

文档摘要：{document_summary}
客户要求：{client_rag}
现有术语库：{terms_json}

文档内容：
{sample_text}

输出JSON格式：
{{
  "terms": [
    {{"source": "人工智能", "target": "", "context": "技术术语", "priority": "high"}}
  ]
}}`,
  translation: `你是专业译者，请将以下内容从 {source_lang} 翻译到 {target_lang}。

**核心指令：**
1. **目标语言：{target_lang}**。无论原文是什么语言，你的输出必须是**纯粹的{target_lang}**。
2. **意译优先**：在保持原意的前提下，用符合{target_lang}习惯的自然表达。允许调整语序、补充省略成分、转换被动为主动。例外：品牌名、专有名词、数字保留原文形式。
3. **保持格式标签**。原文中的 {1}...{/1} 标签、字幕 ASS 标签（如 {\\an8}、{\\b1}、{\\i1} 等）必须原样保留在译文中对应位置。
{domain_instructions}
{reference_section}
{anchor_section}
{context_window_section}
上下文参考：
文档摘要：{document_summary}
客户要求：{client_rag}
{terms_section}
风格指南：{house_style}
{identified_terms_section}

翻译要求：
1. 保持术语一致性
2. 遵循客户风格偏好
3. 考虑文档整体语境
4. **只输出目标语言的纯译文，不要输出任何解释、说明或原文。**
5. **不要将译文包裹在引号中，也不要添加 Markdown 标题。**
6. **非常重要 - 格式标签（如果原文有）：**
   - **如果原文中有形如 {1}文本{/1} 的格式标签，必须在译文中严格保持这些标签的完整结构。**
   - **字幕文件可能含 ASS 标签（形如 {\\an8}、{\\b1}、{\\i1}、{\\c&HFFFFFF&} 等，以反斜杠开头），必须原样保留在译文中对应位置，严禁翻译、改写或丢弃。**
   - **如果原文中没有格式标签，译文也不能添加任何标签。**
   - **结束标签必须有斜杠：{/n}，不能写成 /n 或 {n}。**
   - **即使标签内没有文本（如 {1}{/1}），也必须原样保留。**
   - **不能删除原文已有的标签，也不能添加原文中不存在的标签。**
例如：原文 "Note {1}{/1}" -> 译文 "注意 {1}{/1}" (必须有斜杠)
例如：原文 "Click {1}here{/1}" -> 译文 "点击{1}这里{/1}"
例如：原文 "{\\an8}顶部字幕" -> 译文 "{\\an8}Top subtitle" (ASS 标签原样保留)
例如：原文 "普通文本" -> 译文 "普通文本" (原文无标签，译文也不应有)

原文：{content}`,
  reflection: `你是专业译者，请对当前译文进行反思并产出优化版本（参考 VideoLingo 两步翻译的反思机制）。

**原文**：{source_text}
**当前译文**：{translation}
**目标语言**：{target_lang}

**MQM 评估发现的问题**：
{issues_json}

**改进建议**：{suggestion}

**反思维度**（针对当前文档类型 {document_type}）：
{reflect_dimensions}

**输出要求**：严格输出以下 JSON 格式（不要输出任何其他内容，不要 Markdown 标题，不要代码块）：
{{
  "reflect": "对当前译文的问题分析（针对上述反思维度，指出 literal 翻译、翻译腔、术语不一致、风格偏差等问题）",
  "free": "反思后的优化译文（必须保留原文所有格式标签 {1}{/1} 和 ASS 标签 {\\an8} 等的结构）"
}}

**注意**：
1. reflect 字段是问题分析，free 字段是优化后的译文
2. free 字段必须只包含译文本身，不要解释、不要原文
3. 格式标签必须严格保留：{1}...{/1}、{\\an8}、{\\b1} 等
4. 结束标签必须有斜杠：{/n}
5. 不要新增或删除原文已有的标签`,
  mqm: `基于MQM框架评估以下翻译质量：

原文：{source_text}
译文：{translation}
客户要求：{client_rag}
术语规范：{identified_terms_json}

请评估以下维度（0-1分）：
1. Accuracy（准确性）
2. Fluency（流畅性）
3. Terminology（术语合规）
4. Style（风格一致）

输出JSON格式：
{{
  "accuracy": 0.9,
  "fluency": 0.85,
  "terminology": 0.95,
  "style": 0.88,
  "overallScore": 0.89,
  "issues": ["术语'XX'应使用'YY'"],
  "suggestion": "建议修改为..."
}}`,
  line_alignment: `你之前翻译的内容行数与原文不一致，请重新翻译，**严格保持原文的换行结构**。

**原文（共 {source_line_count} 行）**：
{content}

**当前译文（共 {trans_line_count} 行，行数不对）**：
{translation}

**问题**：原文 {source_line_count} 行，译文 {trans_line_count} 行，必须一致。

**要求**：
1. 译文必须正好 {source_line_count} 行，每行对应原文相应行
2. 不要合并原文的多行，也不要拆分原文的单行
3. 保留所有格式标签 {1}{/1} 和 ASS 标签
4. 只输出译文，不要解释

请输出修正后的译文（{source_line_count} 行）：`,
}

// system prompt 默认值（仅 translation / line_alignment 有）
const DEFAULT_SYSTEM_PROMPT: Record<string, string> = {
  translation: '你是一个精通 {source_lang} 和 {target_lang} 的专业翻译专家。请严格按照指示输出译文。',
  line_alignment: '你是一个精通 {source_lang} 和 {target_lang} 的专业翻译专家。',
}

// 测试场的常见变量默认值，让"直接运行"即可出结果
const DEFAULT_TEST_VARS: Record<string, string> = {
  source_lang: 'en',
  target_lang: 'zh',
  document_type: 'marketing',
  content: 'Welcome to our new boutique in the heart of Wuhan.',
  sample_text: 'This is a sample paragraph about a marketing campaign.\nSecond line for context.',
  source_text: 'Welcome to our new boutique in the heart of Wuhan.',
  translation: '欢迎来到我们武汉中心的新精品店。',
  house_style: '高端、有格调，避免直白描述，使用四字词语增加韵律感。',
  client_rag: '客户品牌调性：高端、有格调。',
  document_summary: '这是一个关于高端精品店开业的市场营销文档。',
  terms_json: '[]',
  identified_terms_json: '[]',
  issues_json: '["术语\"boutique\"应保持一致"]',
  suggestion: '将精品店统一译为"精品店"。',
  reflect_dimensions: '- 品牌调性\n- 文化适配\n- 感染力',
  source_line_count: '2',
  trans_line_count: '1',
  domain_instructions: '',
  reference_section: '',
  anchor_section: '',
  context_window_section: '',
  terms_section: '术语库：本段无相关术语',
  identified_terms_section: '',
}

/** 提取模板中的 {变量} 占位符（排除 {{ 双花括号字面量）。 */
function extractPlaceholders(content: string): string[] {
  const names: string[] = []
  const re = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g
  let m: RegExpExecArray | null
  while ((m = re.exec(content)) !== null) {
    if (!names.includes(m[1])) names.push(m[1])
  }
  return names
}

export function PromptTemplatesPanel() {
  const queryClient = useQueryClient()
  const [activeScene, setActiveScene] = useState('translation')
  // 编辑内容（未保存的草稿），scene -> promptContent
  const [edits, setEdits] = useState<Record<string, string>>({})
  // 测试场变量，scene -> { varName: value }
  const [testVars, setTestVars] = useState<Record<string, Record<string, string>>>({})
  // 测试结果，scene -> 结果对象
  const [testResults, setTestResults] = useState<Record<string, any>>({})

  const { data: listResp, isLoading } = useQuery({
    queryKey: ['prompt-templates'],
    queryFn: () => promptTemplatesApi.getList(),
  })

  const templates = (listResp as any)?.data || []
  const sceneRecords = useMemo(() => {
    const map: Record<string, any> = {}
    templates.forEach((t: any) => { map[t.scene] = t })
    return map
  }, [templates])

  const saveMutation = useMutation({
    mutationFn: (scene: string) =>
      promptTemplatesApi.upsert(scene, { promptContent: edits[scene] ?? '' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompt-templates'] })
      toast.success('模板已保存')
    },
    onError: (error: any) => toast.error(error?.error || '保存失败'),
  })

  const testMutation = useMutation({
    mutationFn: (scene: string) =>
      promptTemplatesApi.test({
        scene,
        promptContent: (edits[scene] ?? '') || undefined,
        systemPrompt: DEFAULT_SYSTEM_PROMPT[scene],
        variables: testVars[scene] || {},
      }),
    onSuccess: (data: any) => {
      setTestResults((prev) => ({ ...prev, [activeScene]: data?.data }))
    },
    onError: (error: any) => {
      toast.error(error?.error || '测试失败')
      setTestResults((prev) => ({ ...prev, [activeScene]: { error: error?.error || '测试失败' } }))
    },
  })

  const currentEdited = edits[activeScene] ?? ''
  const currentDisplay = currentEdited !== '' ? currentEdited : (sceneRecords[activeScene]?.promptContent || DEFAULT_CONTENT[activeScene] || '')
  const currentHasTemplate = !!sceneRecords[activeScene]?.hasTemplate
  const placeholders = useMemo(() => extractPlaceholders(currentDisplay), [currentDisplay])

  // 挂载/模板加载后，初始化当前场景的测试变量（用默认值填充）
  useEffect(() => {
    initTestVars(activeScene)
    // 仅依赖模板加载完成，避免每次编辑都重建
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates])

  /** 初始化某场景的测试变量（用默认值填充后，再覆盖已填写的）。 */
  const initTestVars = (scene: string) => {
    setTestVars((prev) => {
      if (prev[scene]) return prev
      const vars: Record<string, string> = {}
      const placeholders = extractPlaceholders(
        edits[scene] ?? (sceneRecords[scene]?.promptContent || DEFAULT_CONTENT[scene] || '')
      )
      placeholders.forEach((p) => {
        vars[p] = DEFAULT_TEST_VARS[p] ?? ''
      })
      return { ...prev, [scene]: vars }
    })
  }

  const handleSceneChange = (scene: string) => {
    if (scene === activeScene) return
    setActiveScene(scene)
    initTestVars(scene)
  }

  const runTest = () => {
    testMutation.mutate(activeScene)
  }

  return (
    <div className="space-y-6">
      {/* ---------- 1. 外显管理 ---------- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">步骤 Prompt 外显管理</CardTitle>
          <CardDescription>查看/编辑机翻 agent 各步骤的 prompt 模板，保存后对翻译生效（引擎 TTL 缓存约 5 分钟刷新）</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">加载中...</div>
          ) : (
            <Tabs value={activeScene} onValueChange={handleSceneChange}>
              <TabsList className="w-full flex-wrap h-auto">
                {SCENES.map((s) => (
                  <TabsTrigger key={s.id} value={s.id} className="flex-1 min-w-[100px]">
                    {s.name}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value={activeScene} className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{SCENES.find((s) => s.id === activeScene)?.name}</p>
                    <p className="text-xs text-muted-foreground">{SCENES.find((s) => s.id === activeScene)?.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!currentHasTemplate && currentEdited === '' && (
                      <Badge variant="outline">未自定义（用内置默认）</Badge>
                    )}
                    <Button
                      size="sm"
                      onClick={() => saveMutation.mutate(activeScene)}
                      disabled={saveMutation.isPending || currentEdited === ''}
                    >
                      {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                      保存
                    </Button>
                  </div>
                </div>

                <Textarea
                  value={currentDisplay}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [activeScene]: e.target.value }))}
                  className="min-h-[320px] font-mono text-sm leading-relaxed"
                  placeholder="输入该步骤的 prompt 模板正文，{变量} 为占位符"
                />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>

      {/* ---------- 2. 测试场 ---------- */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4" /> 测试场
          </CardTitle>
          <CardDescription>
            当前步骤「{SCENES.find((s) => s.id === activeScene)?.name}」：填入下方 {placeholders.length} 个变量后运行，查看渲染后的完整 prompt 与模型输出
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {placeholders.length === 0 ? (
            <p className="text-sm text-muted-foreground">当前模板没有可渲染的变量占位符。</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {placeholders.map((p) => {
                const vars = testVars[activeScene] || {}
                return (
                  <div key={p} className="space-y-1">
                    <Label className="text-xs font-mono">{p}</Label>
                    <Input
                      value={vars[p] ?? ''}
                      onChange={(e) =>
                        setTestVars((prev) => ({
                          ...prev,
                          [activeScene]: { ...(prev[activeScene] || {}), [p]: e.target.value },
                        }))
                      }
                      placeholder={DEFAULT_TEST_VARS[p] ?? '留空则该项为空'}
                    />
                  </div>
                )
              })}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button onClick={runTest} disabled={testMutation.isPending || placeholders.length === 0}>
              {testMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />}
              运行测试
            </Button>
            {testMutation.isPending && <span className="text-xs text-muted-foreground">正在调用 LLM，可能需要几秒...</span>}
          </div>

          {testResults[activeScene] && (
            <div className="space-y-4">
              {testResults[activeScene].error ? (
                <div className="p-3 rounded-md border border-destructive/50 bg-destructive/5 text-sm text-destructive">
                  {(testResults as any)[activeScene].error}
                </div>
              ) : (
                <>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">渲染后的 Prompt</p>
                    <pre className="p-3 rounded-md border bg-muted/40 text-xs whitespace-pre-wrap break-words max-h-72 overflow-auto">
                      {testResults[activeScene].renderedPrompt}
                    </pre>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">模型输出</p>
                    <pre className="p-3 rounded-md border bg-muted/40 text-xs whitespace-pre-wrap break-words max-h-72 overflow-auto">
                      {testResults[activeScene].output}
                    </pre>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ---------- 3. 自动优化（次要功能） ---------- */}
      <PromptOptimizerPanel />
    </div>
  )
}